import { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import CountryCodeSelect from "../CountryCodeSelect";
import axios from "axios";
import SuccessModal from "./SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import CountryDropdown from '../CountryDropdown';

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function AddEditSiteManagers({
  onSuccess,
  editData = null,
  type = "",
  currentSiteId = null,
} = {}) {
  const { userData } = useContext(AuthContext);
  const isUpdateMode = Boolean(editData);

  // Get display name based on type
  const getDisplayName = () => {
    if (type === "branch") return "Branch";
    if (type === "plant") return "Plant";
    if (type === "location") return "Location";
  };

  const displayName = getDisplayName();

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [, setSites] = useState([]);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    country_code: "+94",
    phone: "",
    email: "",
    password: "",
    bank_account: "",
    site_id: "",
    delete_sales_history: 0,
  });

  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Fetch sites based on type
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
          {
            params: {
              company_code: userData.company_code,
              type: type,
            },
          },
        );

        if (response.data.success) {
          const items = response.data.items || [];
          setSites(items);

          // Select site based on context
          if (!isUpdateMode && items.length > 0) {
            // If currentSiteId is provided, use it; otherwise use first site
            const siteToSelect = currentSiteId
              ? currentSiteId
              : items[0].site_id;

            setFormData((prev) => ({
              ...prev,
              site_id: siteToSelect,
            }));
          }
        }
      } catch (error) {
        console.error(`Error fetching ${type}s:`, error);
      }
    };

    if (userData?.company_code && type) {
      fetchSites();
    }
  }, [userData?.company_code, type, isUpdateMode, editData, currentSiteId]);

  // Initialize form data when editing
  useEffect(() => {
    if (editData) {
      setFormData({
        first_name: editData.first_name || "",
        last_name: editData.last_name || "",
        address_line_1: editData.address_line_1 || "",
        address_line_2: editData.address_line_2 || "",
        city: editData.city || "",
        state: editData.state || "",
        country: editData.country || "",
        country_code: editData.country_code || "+94",
        phone: editData.phone || "",
        email: editData.email || "",
        password: "",
        bank_account: editData.bank_account || "",
        site_id: editData.site_id || "",
        delete_sales_history: editData.delete_sales_history || 0,
      });
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        country: "",
        country_code: "+94",
        phone: "",
        email: "",
        password: "",
        bank_account: "",
        site_id: "",
        delete_sales_history: 0,
      });
    }
  }, [editData]);

  const handleNameChange = (e) => {
    const { name, value } = e.target;
    let cleanValue = value.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, [name]: cleanValue }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      const rules = {
        length: value.length >= 8 && value.length <= 20,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        specialChar: /[\W_]/.test(value),
      };
      setPasswordRules(rules);
    }

    if (name === "phone") {
      // Only allow digits
      const cleanValue = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    // Validate required fields
    if (!formData.first_name || !formData.first_name.trim()) {
      setErrorMsg("First name is required");
      return false;
    }
    if (!formData.last_name || !formData.last_name.trim()) {
      setErrorMsg("Last name is required");
      return false;
    }
    if (!formData.email || !formData.email.trim()) {
      setErrorMsg("Email is required");
      return false;
    }
    if (!formData.phone || !formData.phone.trim()) {
      setErrorMsg("Phone number is required");
      return false;
    }
    if (!formData.site_id) {
      setErrorMsg(
        `${displayName} is not available. Please contact your administrator.`,
      );
      return false;
    }

    // Validate phone number
    if (formData.phone.length < 7 || formData.phone.length > 15) {
      setErrorMsg("Phone number must be 7-15 digits");
      return false;
    }

    // Validate country code
    const countryCode = formData.country_code || "";
    const cleanCountryCode = countryCode.replace(/\D/g, "");
    if (
      !countryCode.startsWith("+") ||
      cleanCountryCode.length < 1 ||
      cleanCountryCode.length > 4
    ) {
      setErrorMsg("Country code must start with + and be 1-4 digits");
      return false;
    }

    // Password validation for create mode
    if (!isUpdateMode) {
      if (!formData.password || !formData.password.trim()) {
        setErrorMsg("Password is required for new manager");
        return false;
      }
      const allRulesPassed = Object.values(passwordRules).every((rule) => rule);
      if (!allRulesPassed) {
        setErrorMsg("Please ensure password meets all requirements");
        return false;
      }
    }

    // Password validation if updating with new password
    if (isUpdateMode && formData.password && formData.password.trim()) {
      const allRulesPassed = Object.values(passwordRules).every((rule) => rule);
      if (!allRulesPassed) {
        setErrorMsg("Please ensure password meets all requirements");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setTimeout(() => setErrorMsg(""), 5000);
      return;
    }

    setIsLoading(true);
    setErrorMsg("");

    try {
      if (isUpdateMode) {
        await updateManager();
      } else {
        await createManager();
      }
    } catch (error) {
      console.error(`${isUpdateMode ? "Update" : "Create"} error:`, error);
      const errorMessage =
        error.message || "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const createManager = async () => {
    const response = await axios.post(
      `${BASE_URL}/api/admin/sites/managers/create-manager`,
      {
        first_name: formData.first_name,
        last_name: formData.last_name,
        address_line_1: formData.address_line_1?.trim() || null,
        address_line_2: formData.address_line_2?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        country: formData.country?.trim() || null,
        country_code: formData.country_code,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        bank_account: formData.bank_account?.trim() || null,
        site_id: formData.site_id,
        delete_sales_history: formData.delete_sales_history,
        company_code: userData.company_code,
      },
      {
        params: {
          company_code: userData.company_code,
          type: type,
        },
      },
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || `Failed to create ${type} manager`,
      );
    }

    showSuccess(`${displayName} manager created successfully!`, "Success");

    // Reset form
    setFormData({
      first_name: "",
      last_name: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      country_code: "+94",
      phone: "",
      email: "",
      password: "",
      bank_account: "",
      site_id: "",
      delete_sales_history: 0,
    });

    setTimeout(() => {
      hideSuccess();
      if (onSuccess && typeof onSuccess === "function") {
        try {
          onSuccess();
        } catch (e) {
          console.error("onSuccess callback error:", e);
        }
      }
    }, 2000);
  };

  const updateManager = async () => {
    const response = await axios.put(
      `${BASE_URL}/api/admin/sites/managers/update-manager/${editData.id}`,
      {
        emp_id: editData.emp_id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        address_line_1: formData.address_line_1?.trim() || null,
        address_line_2: formData.address_line_2?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        country: formData.country?.trim() || null,
        country_code: formData.country_code,
        phone: formData.phone,
        email: formData.email,
        password: formData.password?.trim() || undefined,
        bank_account: formData.bank_account?.trim() || null,
        site_id: formData.site_id,
        delete_sales_history: formData.delete_sales_history,
        company_code: userData.company_code,
        type: type,
      },
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || `Failed to update ${type} manager`,
      );
    }

    showSuccess(`${displayName} manager updated successfully!`, "Success");

    setTimeout(() => {
      hideSuccess();
      if (onSuccess && typeof onSuccess === "function") {
        try {
          onSuccess();
        } catch (e) {
          console.error("onSuccess callback error:", e);
        }
      }
    }, 2000);
  };

  return (
    <>
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
      {errorMsg && (
        <Alert variant="danger" className="text-center">
          {errorMsg}
        </Alert>
      )}

      <Form className="register-form" onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>First Name*</Form.Label>
              <Form.Control
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleNameChange}
                placeholder="Enter first name"
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Last Name*</Form.Label>
              <Form.Control
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleNameChange}
                placeholder="Enter last name"
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Email Address*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number*</Form.Label>
              <div className="d-flex gap-2">
                <CountryCodeSelect
                  value={formData.country_code}
                  onChange={(code) =>
                    setFormData((prev) => ({ ...prev, country_code: code }))
                  }
                  placeholder="Code"
                  isRequired={true}
                  width="100px"
                />
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                  style={{
                    flex: 1,
                    height: "48px",
                    fontSize: "1rem",
                    borderRadius: "12px",
                    backgroundColor: "#f7fafc",
                    border: "2px solid #e2e8f0",
                  }}
                />
              </div>
              <Form.Text className="text-muted">
                Enter phone number with country code (e.g., +94 for Sri Lanka)
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password
                {isUpdateMode ? (
                  <small className="text-muted">
                    {" "}
                    (Leave empty to keep current)
                  </small>
                ) : (
                  "*"
                )}
              </Form.Label>
              <div className="password-wrapper">
                <Form.Control
                  className="password-input"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    isUpdateMode
                      ? "Enter new password (optional)"
                      : "Enter password"
                  }
                  onFocus={() => setShowRules(true)}
                  onBlur={() => setShowRules(false)}
                  required={!isUpdateMode}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {showRules && (
                <div className="password-rules mt-2">
                  <small>Password requirements:</small>
                  <ul className="list-unstyled ms-2">
                    <li
                      style={{
                        color: passwordRules.length ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.length ? "✅" : "❌"} 8-20 characters
                    </li>
                    <li
                      style={{
                        color: passwordRules.uppercase ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.uppercase ? "✅" : "❌"} At least one
                      uppercase letter
                    </li>
                    <li
                      style={{
                        color: passwordRules.lowercase ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.lowercase ? "✅" : "❌"} At least one
                      lowercase letter
                    </li>
                    <li
                      style={{
                        color: passwordRules.number ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.number ? "✅" : "❌"} At least one number
                    </li>
                    <li
                      style={{
                        color: passwordRules.specialChar
                          ? "#28a745"
                          : "#dc3545",
                      }}
                    >
                      {passwordRules.specialChar ? "✅" : "❌"} At least one
                      special character
                    </li>
                  </ul>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Address Line 1 <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
                placeholder="Enter address line 1"
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
                placeholder="Enter address line 2"
              />
            </Form.Group>
          </Col>

          {/* <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Bank Account Details</Form.Label>
              <Form.Control
                type="text"
                name="bank_account"
                value={formData.bank_account}
                onChange={handleChange}
                placeholder="Enter bank account details"
              />
            </Form.Group>
          </Col> */}
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter state"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <CountryDropdown
                value={formData.country || ""}
                onChange={(country) =>
                  setFormData((prev) => ({ ...prev, country: country }))
                }
                placeholder="Select country"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="pt-5">
              <Form.Check
                type="checkbox"
                name="delete_sales_history"
                label="Allow branch manager to delete the sales history"
                checked={formData.delete_sales_history === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    delete_sales_history: e.target.checked ? 1 : 0,
                  }))
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-center">
          <Button type="submit" className="register-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isUpdateMode
                  ? `Updating ${displayName} Manager...`
                  : `Creating ${displayName} Manager...`}
              </>
            ) : isUpdateMode ? (
              `Update ${displayName} Manager`
            ) : (
              `Create ${displayName} Manager`
            )}
          </Button>
        </div>
      </Form>
    </>
  );
}
